import { useRef, useCallback, useEffect } from "react";
import { Step } from "../../types/TransactionTemplate.types";
import {
  BRANCH_YES_OFFSET,
  BRANCH_NO_OFFSET,
  DEFAULT_START_Y,
} from "../../types/editor.constants";

interface DragState {
  stepIndex: number;
  primId: string;
  affectedIds: Set<string>;
  startMouseX: number;
  startMouseY: number;
  originalPositions: Map<string, { x: number; y: number }>;
  originalConnections: Step["connections"];
}

export interface DropTarget {
  branch: "yes" | "no" | "main" | "body";
  parentIfId: string | null;
  insertAfterX: number;
}

export const getLogicalBlock = (step: Step, startId: string): Set<string> => {
  return getLogicalBlockFromConnections(
    step.primitives,
    step.connections,
    startId,
  );
};

export const getLogicalBlockFromConnections = (
  primitives: Step["primitives"],
  connections: Step["connections"],
  startId: string,
): Set<string> => {
  const block = new Set<string>([startId]);
  const prim = primitives.find((p) => p.id === startId);

  if (!prim || (prim.type !== "condition" && prim.type !== "loop")) {
    return block;
  }

  const shouldFollow = (fromId: string, conn: Step["connections"][number]) => {
    if (conn.from !== fromId) return false;
    const fromPrim = primitives.find((p) => p.id === fromId);
    if (fromPrim?.type === "condition") {
      return !!conn.branch;
    }
    if (fromPrim?.type === "loop") {
      return true;
    }
    return true;
  };

  const reachable = new Set<string>();
  const q = [startId];
  while (q.length > 0) {
    const cur = q.shift()!;
    if (reachable.has(cur)) continue;
    reachable.add(cur);
    connections
      .filter((c) => shouldFollow(cur, c))
      .forEach((c) => q.push(c.to));
  }

  const inDegrees: Record<string, number> = {};
  connections.forEach((c) => {
    if (reachable.has(c.to)) {
      inDegrees[c.to] = (inDegrees[c.to] || 0) + 1;
    }
  });

  const visited = new Set<string>();
  const bfsQueue = [startId];
  while (bfsQueue.length > 0) {
    const curr = bfsQueue.shift()!;
    if (visited.has(curr)) continue;
    visited.add(curr);
    if (curr !== startId && (inDegrees[curr] || 0) > 1) continue;
    block.add(curr);
    connections
      .filter((c) => shouldFollow(curr, c))
      .forEach((c) => bfsQueue.push(c.to));
  }

  return block;
};

export function resolveDropTarget(
  primitives: Step["primitives"],
  connections: Step["connections"],
  dropX: number,
  dropY: number,
  affectedIds: Set<string>,
): DropTarget {
  const YES_ZONE_START = BRANCH_YES_OFFSET / 2;
  const NO_ZONE_START = (BRANCH_YES_OFFSET + BRANCH_NO_OFFSET) / 2;

  const branchNodes = primitives.filter(
    (p) =>
      !affectedIds.has(p.id) && (p.type === "condition" || p.type === "loop"),
  );

  let bestIf: (typeof primitives)[0] | null = null;
  let bestXDist = Infinity;

  for (const ifNode of branchNodes) {
    const relY = dropY - ifNode.y;
    if (relY < YES_ZONE_START || relY > BRANCH_NO_OFFSET + 100) continue;

    if (ifNode.type === "loop") {
      if (dropX > ifNode.x) {
        const xDist = dropX - ifNode.x;
        if (xDist < bestXDist) {
          bestXDist = xDist;
          bestIf = ifNode;
        }
      }
      continue;
    }

    const xDist = Math.abs(ifNode.x - dropX);
    if (xDist < bestXDist) {
      bestXDist = xDist;
      bestIf = ifNode;
    }
  }

  if (!bestIf) {
    return { branch: "main", parentIfId: null, insertAfterX: dropX };
  }

  const relY = dropY - bestIf.y;

  if (relY < YES_ZONE_START) {
    return { branch: "main", parentIfId: null, insertAfterX: dropX };
  }

  if (bestIf.type === "loop") {
    return { branch: "body", parentIfId: bestIf.id, insertAfterX: dropX };
  }

  if (relY < NO_ZONE_START) {
    return { branch: "yes", parentIfId: bestIf.id, insertAfterX: dropX };
  } else {
    return { branch: "no", parentIfId: bestIf.id, insertAfterX: dropX };
  }
}

export function getSourceBranch(
  connections: Step["connections"],
  primId: string,
): { branch: "yes" | "no" | "main"; parentIfId: string | null } {
  const incomingConn = connections.find((c) => c.to === primId);
  if (!incomingConn) return { branch: "main", parentIfId: null };

  if (incomingConn.branch === "yes" || incomingConn.branch === "no") {
    return { branch: incomingConn.branch, parentIfId: incomingConn.from };
  }

  return getSourceBranch(connections, incomingConn.from);
}

export function useDragPrimitive(
  steps: Step[],
  isEditMode: boolean,
  setSteps: React.Dispatch<React.SetStateAction<Step[]>>,
  onDropSwap?: (
    stepIndex: number,
    draggedId: string,
    targetId: string,
    originalPositions: Map<string, { x: number; y: number }>,
    originalConnections: Step["connections"],
  ) => void,
  onDropMove?: (
    stepIndex: number,
    draggedId: string,
    dropTarget: DropTarget,
    originalPositions: Map<string, { x: number; y: number }>,
    originalConnections: Step["connections"],
  ) => void,
) {
  const dragState = useRef<DragState | null>(null);
  const isDragging = useRef(false);
  const stepsRef = useRef(steps);

  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  const onPrimMouseDown = useCallback(
    (e: React.MouseEvent, stepIndex: number, primId: string) => {
      if (!isEditMode) return;
      e.stopPropagation();
      e.preventDefault();

      const step = stepsRef.current[stepIndex];
      if (!step) return;

      const affectedIds = getLogicalBlockFromConnections(
        step.primitives,
        step.connections,
        primId,
      );

      const originalPositions = new Map<string, { x: number; y: number }>();
      step.primitives.forEach((p) => {
        originalPositions.set(p.id, { x: p.x, y: p.y });
      });

      dragState.current = {
        stepIndex,
        primId,
        affectedIds,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        originalPositions,
        originalConnections: step.connections.map((c) => ({ ...c })),
      };
      isDragging.current = false;
    },
    [isEditMode],
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState.current) return;

      const dx = e.clientX - dragState.current.startMouseX;
      const dy = e.clientY - dragState.current.startMouseY;

      if (!isDragging.current && Math.abs(dx) + Math.abs(dy) > 5) {
        isDragging.current = true;
      }
      if (!isDragging.current) return;

      const { stepIndex, affectedIds, originalPositions, originalConnections } =
        dragState.current;

      setSteps((prev) => {
        const newSteps = [...prev];
        newSteps[stepIndex] = {
          ...newSteps[stepIndex],
          primitives: newSteps[stepIndex].primitives.map((p) => {
            if (!affectedIds.has(p.id)) return p;
            const orig = originalPositions.get(p.id)!;
            return { ...p, x: orig.x + dx, y: orig.y + dy };
          }),
          connections: originalConnections.filter((c) => {
            const fromInside = affectedIds.has(c.from);
            const toInside = affectedIds.has(c.to);
            return !(fromInside !== toInside);
          }),
        };
        return newSteps;
      });
    },
    [setSteps],
  );

  const onMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!dragState.current) return;

      let handled = false;

      if (isDragging.current) {
        const {
          stepIndex,
          primId,
          affectedIds,
          originalPositions,
          startMouseX,
          startMouseY,
          originalConnections,
        } = dragState.current;

        const dx = e.clientX - startMouseX;
        const dy = e.clientY - startMouseY;
        const origPos = originalPositions.get(primId);
        const dragDistance = Math.sqrt(dx * dx + dy * dy);

        if (origPos && dragDistance > 20) {
          const dragCenterX = origPos.x + dx + 60;
          const dragCenterY = origPos.y + dy + 40;

          const step = stepsRef.current[stepIndex];
          if (step) {
            const source = getSourceBranch(originalConnections, primId);

            const dropTarget = resolveDropTarget(
              step.primitives,
              originalConnections,
              dragCenterX,
              dragCenterY,
              affectedIds,
            );

            const isCrossBranch =
              dropTarget.branch !== source.branch ||
              dropTarget.parentIfId !== source.parentIfId;

            if (onDropMove) {
              onDropMove(
                stepIndex,
                primId,
                dropTarget,
                originalPositions,
                originalConnections,
              );
              handled = true;
            }
          }
        }
      }

      if (!handled && dragState.current) {
        const { stepIndex, originalPositions, originalConnections } =
          dragState.current;
        setSteps((prev) => {
          const newSteps = [...prev];
          newSteps[stepIndex] = {
            ...newSteps[stepIndex],
            primitives: newSteps[stepIndex].primitives.map((p) => {
              const orig = originalPositions.get(p.id);
              return orig ? { ...p, x: orig.x, y: orig.y } : p;
            }),
            connections: originalConnections.map((c) => ({ ...c })),
          };
          return newSteps;
        });
      }

      dragState.current = null;
      isDragging.current = false;
    },
    [onDropSwap, onDropMove, setSteps],
  );

  const getIsDragging = useCallback(() => isDragging.current, []);

  return { onPrimMouseDown, onMouseMove, onMouseUp, getIsDragging };
}
